import PropTypes from 'prop-types';
import React, { useEffect, useState } from "react";

import Label from '../formsUi/common/label/Label';
import ImageUpload from '../formsUi/imageUpload/ImageUpload';
import ValidationMessage from '../formsUi/common/validation/ValidationMessage';

/**
 * A form field component for handling image uploads with validation and action handling.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.labelName] - The label for the image upload field.
 * @param {string} [props.name] - The name attribute for the file input.
 * @param {Function} [props.onChange] - Callback function when an image is selected or input value changes.
 * @param {string} [props.dataField] - Data field identifier for the image input.
 * @param {string} [props.error] - Error message to display.
 * @param {Object} [props.formSetting] - Settings for the form, such as view-only mode.
 * @param {Object} [props.overRideProps] - Properties to override default behavior.
 * @param {Object} [props.inputProps] - Additional props passed to the ImageUpload component.
 */
const FormImageUploadControls = ({
    labelName,
    name,
    onChange,
    onImageFieldBlur,
    dataField,
    error,
    formSetting,
    formData,
    isRequired,
    overRideProps,
    onActionChange,
    fieldSetting,
    ...inputProps
}) => {

    let imageFieldValue = formData?.[dataField];

    const [imageValues, setImageValues] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);

    const { allowMultipleImage, acceptedImageFormats, isCircularPreview } = fieldSetting;

    const handleInputChange = (e) => {
        if (!onChange) return;

        if (e.target.files.length > 0) {
            let newFiles = Array.from(e.target.files);

            if (!allowMultipleImage) {
                processNewImages([newFiles[0]]);
            } else {
                processNewImages(newFiles);
            }

        } else {
            setImageValues([]);
            onChange(dataField, null);
        }
    };

    const processNewImages = (newFiles) => {
        // Process only the newly added files
        const imagePromises = newFiles.map(readFileAsDataURL);

        Promise.all(imagePromises).then((imageDataArray) => {
            const newImageObjects = imageDataArray.map((imageData, index) => ({
                file: newFiles[index],
                fileName: newFiles[index].name,
                base64Data: imageData.base64Data,
                image: imageData.img
            }));

            // Combine with already uploaded images
            const allUploadedImages = allowMultipleImage
                ? [...imageValues, ...newImageObjects]
                : newImageObjects;

            // Update states and trigger events
            const imagesToPass = allowMultipleImage ? allUploadedImages : newImageObjects[0];
            onChange(dataField, imagesToPass);
            handleOnBlur(imagesToPass);
            setImageValues(allUploadedImages);

            setImagePreviews(() => {
                let newImageArray = Array.isArray(imagesToPass) ? imagesToPass : [imagesToPass]
                return newImageArray.map(image => image.base64Data)
            })
        });
    };

    const handleOnBlur = (files) => {
        if (!files) return;
        const filesArray = Array.isArray(files) ? files : [files];

        onImageFieldBlur?.(dataField, filesArray, fieldSetting);
    };

    const readFileAsDataURL = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                // resolve(reader.result)
                handleReadImage(reader.result, file, resolve)
            };
        });
    };

    const handleReadImage = (imageDataUrl, file, resolve) => {
        const img = new Image();
        img.src = imageDataUrl;
        img.onload = () => {
            const updatedImageData = {
                img,
                file,
                fileName: file.name,
                base64Data: imageDataUrl
            }
            resolve(updatedImageData);

        };
    }

    const handleClearImage = (imageIndex) => {

        const newImageValues = [...imageValues];
        const newImagePreviews = [...imagePreviews];

        newImageValues.splice(imageIndex, 1);
        newImagePreviews.splice(imageIndex, 1);

        setImageValues(newImageValues);
        setImagePreviews(newImagePreviews);

        onChange(dataField, newImageValues);

        onActionChange?.("CLEAR");
    };

    const handleClearAll = () => {
        setImageValues([]);
        setImagePreviews([]);
        onChange(dataField, null);
    }

    const handleActionClick = (action) => {
        if (onActionChange) {
            onActionChange(action, dataField);
        }
    };

    useEffect(() => {
        if (imageFieldValue) {

            if (typeof imageFieldValue === 'string') {
                setImagePreviews([imageFieldValue]);
            }
            else if (Array.isArray(imageFieldValue)) {

                const previews = imageFieldValue.map(item => item.base64Data).filter(Boolean);
                setImagePreviews(previews);
            } else if (imageFieldValue.base64Data) {
                setImagePreviews([imageFieldValue.base64Data])
            }
        }
        else {
            setImagePreviews([])
        }
    }, [imageFieldValue]);

    useEffect(() => {
        if (error && imageValues.length > 0) {
            setImageValues([])
            setImagePreviews([]);
        }
    }, [error, imageValues]);

    return (
        <>
            <div className="input-label-part">
                {labelName && labelName !== "" && (
                    <Label labelName={labelName} for={name} isRequired={isRequired} />
                )}
                <ImageUpload
                    onClear={handleClearImage}
                    onClearAllImages={handleClearAll}
                    selectedImagePreviews={imagePreviews}
                    onChange={handleInputChange}
                    onActionClick={handleActionClick}
                    onBlur={handleOnBlur}
                    isDisable={formSetting?.isViewOnly || inputProps?.isDisable || false || overRideProps?.isDisable}
                    isMultipleImageAllowed={allowMultipleImage}
                    isCircularPreview={isCircularPreview}
                    isDownloadButton={fieldSetting?.isDownloadable}
                    isViewButton={fieldSetting?.isViewable !== false && (fieldSetting?.isViewButton ?? true)}
                    acceptedImageFormats={acceptedImageFormats}
                    placeholder={fieldSetting?.placeholder}
                    fieldSetting={fieldSetting}
                />
            </div>
            <ValidationMessage error={error || ""} />
        </>
    );
};

FormImageUploadControls.propTypes = {
    labelName: PropTypes.string,
    name: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onImageFieldBlur: PropTypes.func,
    dataField: PropTypes.string.isRequired,
    error: PropTypes.string,
    formSetting: PropTypes.shape({
        isViewOnly: PropTypes.bool
    }),
    isRequired: PropTypes.bool,
    overRideProps: PropTypes.shape({
        isDisable: PropTypes.bool
    }),
    formData: PropTypes.object,
    onActionChange: PropTypes.func,
    fieldSetting: PropTypes.shape({
        maxImageHeight: PropTypes.number,
        maxImageWidth: PropTypes.number,
        minImageHeight: PropTypes.number,
        minImageWidth: PropTypes.number,
        minImageSizeInMB: PropTypes.number,
        maxImageSizeInMB: PropTypes.number,
        allowMultipleImage: PropTypes.bool,
        acceptedImageFormats: PropTypes.arrayOf(PropTypes.string),
        placeholder: PropTypes.string,
        isDownloadable: PropTypes.bool,
        isViewable: PropTypes.bool,
        isViewButton: PropTypes.bool,
    }).isRequired,
    inputProps: PropTypes.shape({
        value: PropTypes.any,
        isDisable: PropTypes.bool,
        isRequired: PropTypes.bool,
        isDownloadable: PropTypes.bool,
        isViewButton: PropTypes.bool,
    })
};

export default FormImageUploadControls;